import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const ParklandFormula = ({ onNext, onBack, onDataChange, patientWeight, tbsaValue, data }) => {
  const [weight, setWeight] = useState(data?.weight || patientWeight || '75');
  const [tbsa, setTbsa] = useState(data?.tbsa || tbsaValue || '');
  const [startTime, setStartTime] = useState(data?.startTime || '');
  const [urineOutput, setUrineOutput] = useState(data?.urineOutput || '');
  const [calculations, setCalculations] = useState(data?.calculations || {
    totalFluid: 0,
    first8hRate: 0,
    next16hRate: 0
  });

  // Auto-populate TBSA from tbsaValue when it changes
  React.useEffect(() => {
    if (tbsaValue && !data?.tbsa) {
      setTbsa(tbsaValue);
    }
  }, [tbsaValue, data?.tbsa]);

  // Auto-calculate when weight or TBSA changes
  useEffect(() => {
    if (weight && tbsa) {
      const totalFluid = 4 * parseFloat(weight) * parseFloat(tbsa);
      const first8hRate = (totalFluid / 2) / 8;
      const next16hRate = (totalFluid / 2) / 16;
      
      setCalculations({
        totalFluid: Math.round(totalFluid),
        first8hRate: Math.round(first8hRate),
        next16hRate: Math.round(next16hRate)
      });
    }
  }, [weight, tbsa]);

  const handleNext = () => {
    const data = { weight, tbsa, startTime, urineOutput, calculations };
    if (onDataChange) {
      onDataChange({ fluid_management: data });
    }
    onNext();
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-black py-4">
      <div className="w-full max-w-sm mx-auto px-4">
        <div className="flex flex-col min-h-screen bg-zinc-900 rounded-3xl border-2 border-orange-500/30 shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b-2 border-orange-500/30">
            <Button onClick={onBack} variant="ghost" className="flex items-center gap-2 text-orange-500 text-lg font-bold">
              <ArrowLeft className="w-6 h-6" /> Back
            </Button>
            <h1 className="text-lg font-black text-orange-500">Fluid Management</h1>
            <div className="w-20" />
          </div>

          <div className="flex-grow bg-black overflow-y-auto">
            <main className="p-4 space-y-5">
              {/* Parkland Formula */}
              <section>
                <h2 className="text-2xl font-black text-orange-400 mb-2">Parkland Formula</h2>
                <p className="text-base text-orange-200 mb-4">Adults: 4 mL x kg x %TBSA</p>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-lg font-bold text-orange-300 mb-2 block">Patient Weight (kg)</Label>
                    <Input
                      type="number"
                      placeholder="Weight in kg"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      className="w-full rounded-xl border-2 border-orange-500/30 bg-zinc-900 p-4 text-lg text-orange-100"
                    />
                  </div>
                  <div>
                    <Label className="text-lg font-bold text-orange-300 mb-2 block">%TBSA Burned</Label>
                    <Input
                      type="number"
                      placeholder="Enter TBSA %"
                      value={tbsa}
                      onChange={(e) => setTbsa(e.target.value)}
                      className="w-full rounded-xl border-2 border-orange-500/30 bg-zinc-900 p-4 text-lg text-orange-100"
                    />
                  </div>
                  <div>
                    <Label className="text-lg font-bold text-orange-300 mb-2 block">Starting Time</Label>
                    <Input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="w-full rounded-xl border-2 border-orange-500/30 bg-zinc-900 p-4 text-lg text-orange-100"
                    />
                  </div>
                </div>

                {/* Calculation Results */}
                <div className="mt-5 rounded-2xl bg-orange-900/30 border-2 border-orange-500 p-5">
                  <h3 className="font-black text-xl text-orange-300 mb-3">Auto-Calculated Results</h3>
                  <div className="space-y-2 text-lg text-orange-100">
                    <p><span className="font-bold">Total Fluid (24h):</span> {calculations.totalFluid > 0 ? `${calculations.totalFluid} mL` : '-- mL'}</p>
                    <p><span className="font-bold">First 8h Rate:</span> {calculations.first8hRate > 0 ? `${calculations.first8hRate} mL/hr` : '-- mL/hr'}</p>
                    <p><span className="font-bold">Next 16h Rate:</span> {calculations.next16hRate > 0 ? `${calculations.next16hRate} mL/hr` : '-- mL/hr'}</p>
                  </div>
                </div>
              </section>

              {/* Urine Output Goals */}
              <section>
                <h2 className="text-2xl font-black text-orange-400 mb-2">Urine Output Goals</h2>
                <p className="text-base text-orange-200 mb-4">Adults: 0.5-1 mL/kg/hr</p>
                <Label className="text-lg font-bold text-orange-300 mb-2 block">Current Urine Output (mL/hr)</Label>
                <Input
                  type="number"
                  placeholder="Enter mL/hr"
                  value={urineOutput}
                  onChange={(e) => setUrineOutput(e.target.value)}
                  className="w-full rounded-xl border-2 border-orange-500/30 bg-zinc-900 p-4 text-lg text-orange-100"
                />
              </section>
            </main>
          </div>

          <div className="p-4 border-t-2 border-orange-500/30 bg-zinc-900">
            <Button onClick={handleNext} className="w-full h-14 bg-orange-500 text-white text-lg font-black rounded-xl hover:bg-orange-600 shadow-xl">
              Next <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParklandFormula;